import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { onDataChanged } from '../utils/dataBus';
import { localToday, localHaceNDias, isoToLocalDateStr } from '../utils/dateUtils';

const METRICAS = [
  { key: 'tareasCreadas',        label: 'Tareas creadas',        color: 'var(--blue)' },
  { key: 'tareasCompletadas',    label: 'Tareas completadas',    color: 'var(--green)' },
  { key: 'clientesNuevos',       label: 'Clientes nuevos',       color: '#7c3aed' },
  { key: 'expedientesNuevos',    label: 'Expedientes nuevos',    color: '#7c3aed' },
  { key: 'tramitesNuevos',       label: 'Trámites nuevos',       color: '#f59e0b' },
  { key: 'eventosCreados',       label: 'Eventos agendados',     color: '#0d9488' },
  { key: 'audienciasRealizadas', label: 'Audiencias realizadas', color: '#ef4444' },
];

const RANGOS = [7, 14, 30, 90];

function diaVacio() {
  return { tareasCreadas: 0, tareasCompletadas: 0, clientesNuevos: 0, expedientesNuevos: 0, tramitesNuevos: 0, eventosCreados: 0, audienciasRealizadas: 0 };
}

export default function ProductividadPanel() {
  const [open, setOpen] = useState(false);
  const [rango, setRango] = useState(30);
  const [metrica, setMetrica] = useState('tareasCompletadas');
  const [porDia, setPorDia] = useState({}); // { 'YYYY-MM-DD': {..counters} }
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) fetchProductividad(); }, [open, rango]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) return; return onDataChanged(() => fetchProductividad(), ['expedientes', 'tramites', 'clientes', 'agenda', 'tareas']); }, [open, rango]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchProductividad() {
    setLoading(true);
    const desde = localHaceNDias(rango);

    const [
      { data: tareasCreadas },
      { data: tareasCompletadas },
      { data: clientes },
      { data: expedientes },
      { data: tramites },
      { data: eventosCreados },
      { data: audienciasRealizadas },
    ] = await Promise.all([
      supabase.from('tareas').select('created_at').gte('created_at', desde).eq('eliminado', false),
      supabase.from('tareas').select('completada_en').gte('completada_en', desde).eq('eliminado', false),
      supabase.from('clientes').select('created_at').gte('created_at', desde).eq('eliminado', false),
      supabase.from('expedientes').select('created_at').gte('created_at', desde).eq('eliminado', false),
      supabase.from('tramites').select('created_at').gte('created_at', desde).eq('eliminado', false),
      supabase.from('agenda').select('created_at').gte('created_at', desde).eq('eliminado', false),
      supabase.from('agenda').select('fecha').gte('fecha', desde.slice(0, 10)).eq('estado', 'realizado').eq('eliminado', false),
    ]);

    const mapa = {};
    const acumular = (rows, campoFecha, key, esFechaPlain) => {
      (rows || []).forEach(r => {
        const dia = esFechaPlain ? r[campoFecha] : isoToLocalDateStr(r[campoFecha]);
        if (!dia) return;
        if (!mapa[dia]) mapa[dia] = diaVacio();
        mapa[dia][key]++;
      });
    };
    acumular(tareasCreadas, 'created_at', 'tareasCreadas');
    acumular(tareasCompletadas, 'completada_en', 'tareasCompletadas');
    acumular(clientes, 'created_at', 'clientesNuevos');
    acumular(expedientes, 'created_at', 'expedientesNuevos');
    acumular(tramites, 'created_at', 'tramitesNuevos');
    acumular(eventosCreados, 'created_at', 'eventosCreados');
    acumular(audienciasRealizadas, 'fecha', 'audienciasRealizadas', true);

    setPorDia(mapa);
    setLoading(false);
  }

  // Serie completa del rango (incluye días en 0, para que el gráfico no
  // "salte" días sin actividad).
  const serie = useMemo(() => {
    const dias = [];
    const hoy = localToday();
    for (let i = rango - 1; i >= 0; i--) {
      const d = new Date(hoy + 'T12:00:00');
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      dias.push({ fecha: key, label: d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' }), ...(porDia[key] || diaVacio()) });
    }
    return dias;
  }, [porDia, rango]);

  const totales = useMemo(() => {
    const t = diaVacio();
    serie.forEach(d => METRICAS.forEach(m => { t[m.key] += d[m.key]; }));
    return t;
  }, [serie]);

  const maxValor = Math.max(...serie.map(d => d[metrica]), 1);
  const metricaInfo = METRICAS.find(m => m.key === metrica);
  const promedio = (totales[metrica] / rango).toFixed(1);

  // Con muchos días, mostrar una etiqueta cada tanto para no amontonar texto.
  const cadaCuanto = rango <= 14 ? 1 : rango <= 30 ? 3 : 7;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span className="card-title">📈 Productividad histórica</span>
        <span style={{ color: 'var(--text4)', fontSize: 14 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ padding: 20 }}>
          {/* Selector de rango */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {RANGOS.map(r => (
              <button key={r} onClick={() => setRango(r)}
                className={`btn btn-sm ${rango === r ? 'btn-primary' : 'btn-ghost'}`}>
                {r} días
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>Cargando…</div>
          ) : (
            <>
              {/* Totales del período */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1, background: 'var(--border)', marginBottom: 20 }}>
                {METRICAS.map(m => (
                  <div key={m.key} onClick={() => setMetrica(m.key)}
                    style={{
                      background: metrica === m.key ? 'var(--surface2)' : 'var(--surface)',
                      padding: '12px 14px', textAlign: 'center', cursor: 'pointer',
                      borderBottom: metrica === m.key ? `2px solid ${m.color}` : '2px solid transparent',
                    }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: m.color, fontFamily: 'DM Mono' }}>{totales[m.key]}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text4)', marginTop: 3 }}>{m.label}</div>
                  </div>
                ))}
              </div>

              {/* Gráfico de la métrica seleccionada */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{metricaInfo.label} — últimos {rango} días</div>
                <div style={{ fontSize: 11, color: 'var(--text4)' }}>promedio: {promedio}/día</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: rango > 30 ? 2 : 4, height: 120, overflowX: 'auto', paddingBottom: 4 }}>
                {serie.map((d, i) => (
                  <div key={d.fecha} title={`${d.label}: ${d[metrica]}`} style={{ flex: 1, minWidth: rango > 30 ? 4 : 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{
                      width: '100%',
                      background: d[metrica] > 0 ? metricaInfo.color : 'var(--border)',
                      borderRadius: '3px 3px 0 0',
                      height: `${Math.max((d[metrica] / maxValor) * 90, d[metrica] > 0 ? 3 : 1)}px`,
                      opacity: d[metrica] > 0 ? 1 : 0.4,
                    }} />
                    {i % cadaCuanto === 0 && (
                      <div style={{ fontSize: 9, color: 'var(--text4)', whiteSpace: 'nowrap', transform: rango > 14 ? 'rotate(-45deg)' : 'none', marginTop: rango > 14 ? 4 : 0 }}>
                        {d.label}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
